import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Panel, LoadingState, ErrorState } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Archive } from "lucide-react";
import { toast } from "sonner";
import { utc } from "@/components/sync/syncText";

const td = "px-2 py-1 border-t border-[var(--border-subtle)] align-top";
const sel = "h-7 text-xs bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded";
const stepUp = async (code) => (await api.post("/auth/broker/portfolio-step-up", { totp_code: code })).data.step_up_token;

function CodeInput({ value, onChange, testId }) {
  return <Input data-testid={testId} inputMode="numeric" maxLength={6} value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" className="h-7 w-28 text-xs bg-[var(--bg-canvas)] border-[var(--border-subtle)]" />;
}

function PolicyTable({ p }) {
  return (
    <div className="overflow-x-auto">
      <table data-testid="rt-policy" className="w-full text-[11px] text-slate-400">
        <thead><tr className="text-left text-slate-500">{["Records", "Retention", "Total", "Past deadline (deletable)", "On hold", "Manual deletion"].map((h) => <th key={h} className="px-2 py-1 font-medium">{h}</th>)}</tr></thead>
        <tbody>{p.categories.map((c) => (
          <tr key={c.key} data-testid={`rt-cat-${c.key}`}>
            <td className={`${td} text-slate-200`}>{c.label}</td><td className={td}>{c.retention}</td><td className={td}>{c.total}</td>
            <td className={td} data-testid={`rt-cat-${c.key}-eligible`}>{c.eligible}</td><td className={td}>{c.held}</td>
            <td className={td}>{c.deletable ? "owner + fresh code" : "not offered"}</td>
          </tr>))}
        </tbody>
      </table>
      <p data-testid="rt-backup-note" className="mt-2 text-[10px] text-slate-500">{p.backup_note} Automatic deletion: {p.automatic_deletion ? "ON" : "OFF"}.</p>
      {Object.values(p.reintroduced_after_deletion || {}).some((n) => n > 0) && <p data-testid="rt-reintroduced" className="mt-1 text-[11px] text-amber-300">Records deleted earlier exist again (likely restored from a backup): {Object.entries(p.reintroduced_after_deletion).filter(([, n]) => n).map(([k, n]) => `${k} ${n}`).join(", ")}. Review before deleting again.</p>}
    </div>
  );
}

function DeleteForm({ p }) {
  const qc = useQueryClient();
  const elig = p.categories.filter((c) => c.eligible > 0);
  const [cat, setCat] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  if (!p.key_ready) return <div data-testid="rt-delete-disabled" className="text-[11px] text-amber-300">Deletion is disabled until the P3.6 encryption key is configured.</div>;
  if (!p.deletion_enabled) return <div data-testid="rt-delete-switch-off" className="text-[11px] text-amber-300">Permanent deletion is switched off on this server.</div>;
  if (!elig.length) return <div data-testid="rt-delete-none" className="text-[11px] text-emerald-300">No records are past their retention deadline. Nothing can be deleted.</div>;
  const c = elig.find((x) => x.key === cat);
  const go = async () => {
    try {
      const { data } = await api.post("/retention/delete", { category: cat, preview_token: c.preview_token, confirm, step_up_token: await stepUp(code) });
      toast.success(`Deleted ${data.deleted} record(s)`);
      qc.invalidateQueries({ queryKey: ["rt-policy"] });
      qc.invalidateQueries({ queryKey: ["rt-audit"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setCode(""); }
  };
  return (
    <div data-testid="rt-delete" className="flex flex-wrap items-center gap-2 text-[11px]">
      <select data-testid="rt-delete-cat" value={cat} onChange={(e) => setCat(e.target.value)} className={sel}><option value="">Choose records</option>{elig.map((x) => <option key={x.key} value={x.key}>{`${x.label} (${x.eligible})`}</option>)}</select>
      <Input data-testid="rt-delete-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={`Type ${p.confirm_phrase}`} className="h-7 w-48 text-xs bg-[var(--bg-canvas)] border-[var(--border-subtle)]" />
      <CodeInput value={code} onChange={setCode} testId="rt-delete-code" />
      <Button size="sm" data-testid="rt-delete-btn" disabled={!c || confirm !== p.confirm_phrase || code.length !== 6} onClick={go} className="h-7 bg-rose-700 hover:bg-rose-600 text-white">Permanently delete</Button>
    </div>
  );
}

function ExportForm({ ready }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ dataset: "sync_history", format: "csv", scope: "real" });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["rt-exports"], queryFn: async () => (await api.get("/retention/exports")).data });
  const go = async () => {
    setBusy(true);
    try {
      const { data: e } = await api.post("/retention/exports", { ...f, step_up_token: await stepUp(code) });
      const r = await api.get(`/retention/exports/${e.export_id}/download`, { headers: { "X-Download-Token": e.download_token }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `tradify-${f.dataset}-${e.export_id}.${f.format}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Exported ${e.rows} row(s)${e.truncated ? " (truncated)" : ""}`);
      qc.invalidateQueries({ queryKey: ["rt-exports"] });
      qc.invalidateQueries({ queryKey: ["rt-audit"] });
      qc.invalidateQueries({ queryKey: ["rt-policy"] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setCode(""); setBusy(false); }
  };
  const opt = (k, vals) => <select data-testid={`rt-export-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={sel}>{vals.map((v) => <option key={v} value={v}>{v}</option>)}</select>;
  return (
    <div className="space-y-2 text-[11px] text-slate-400">
      <div className="flex flex-wrap items-center gap-2">
        {opt("dataset", ["sync_history", "snapshot_metadata", "ledger_entries", "holdings_register", "retention_audit"])}{opt("format", ["csv", "json"])}{opt("scope", ["real", "demo"])}
        <CodeInput value={code} onChange={setCode} testId="rt-export-code" />
        <Button size="sm" data-testid="rt-export-btn" disabled={!ready || code.length !== 6 || busy} onClick={go} className="h-7 bg-blue-600 hover:bg-blue-500 text-white">Create & download</Button>
      </div>
      <div className="text-[10px] text-slate-500">Encrypted at rest, one download within 10 minutes, deleted automatically after 24 hours. No passwords, keys, tokens or account IDs.</div>
      <ul data-testid="rt-exports" className="space-y-0.5">{data?.exports.map((x) => <li key={x.export_id} data-testid={`rt-export-${x.export_id}`}>{x.dataset} · {x.format} · {x.scope} · {x.rows} rows · expires {utc(x.expires_at)} · {x.downloaded ? "downloaded" : x.link_expired ? "link expired" : "not downloaded"}</li>)}</ul>
    </div>
  );
}

function AuditList() {
  const { data } = useQuery({ queryKey: ["rt-audit"], queryFn: async () => (await api.get("/retention/audit")).data });
  if (!data) return null;
  return (
    <div className="space-y-1 text-[11px] text-slate-400">
      <div data-testid="rt-chain" className={data.chain.ok ? "text-emerald-300" : "text-rose-300"}>Audit chain {data.chain.ok ? "intact" : `broken at #${data.chain.broken_at}`} · {data.chain.records} record(s)</div>
      {data.chain.head && <div data-testid="rt-chain-head" className="font-mono text-[10px] text-slate-500 break-all">Latest seal (note it down to detect rewrites): {data.chain.head}</div>}
      <ul data-testid="rt-audit">{data.rows.map((r) => <li key={r.seq}>#{r.seq} · {utc(r.created_at)} · {r.action}</li>)}</ul>
    </div>
  );
}

export default function Retention() {
  const { data: p, isLoading, isError, refetch } = useQuery({ queryKey: ["rt-policy"], queryFn: async () => (await api.get("/retention/policy")).data });
  return (
    <div data-testid="retention-page" className="space-y-4">
      <PageHeader title="Retention & Secure Exports" subtitle="Manual retention only — nothing is deleted automatically; legal holds always win" icon={Archive} demo={false} testId="retention-header" />
      {isLoading && <LoadingState label="Loading retention policy…" />}
      {isError && <ErrorState label="Retention policy could not be loaded" onRetry={refetch} />}
      {p && (
        <>
          {!p.key_ready && <div data-testid="rt-key-missing" className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200">Exports, legal holds and deletion are switched off: the P3.6 encryption key is not configured on this server. Viewing the policy still works.</div>}
          {p.key_ready && !p.exports_enabled && <div data-testid="rt-exports-off" className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200">Exports are switched off on this server (P3_6_EXPORTS_ENABLED is not "true").</div>}
          <Panel title="Retention policy & deletion preview" testId="rt-policy-panel"><div className="p-4"><PolicyTable p={p} /></div></Panel>
          <Panel title="Permanent deletion (manual)" testId="rt-delete-panel"><div className="p-4"><DeleteForm p={p} /></div></Panel>
          <Panel title="Secure exports" testId="rt-export-panel"><div className="p-4"><ExportForm ready={p.key_ready && p.exports_enabled} /></div></Panel>
          <Panel title="Retention audit" testId="rt-audit-panel"><div className="p-4"><AuditList /></div></Panel>
        </>
      )}
    </div>
  );
}
